import * as React from 'react';
import {StyleProp, Text, TouchableOpacity, View, ViewStyle, Platform, ColorValue} from 'react-native';
import {images} from '../../../assets/icons';
import {Image} from '../../../shared/components/image';
import {useTheme} from '../../../shared/theme';
import {stripHTML} from '../../../shared/pipes';
import {useAlign, AlignFunc, DeviceSize} from '../../../shared/theme/helpers';
import {Theme} from '../../../shared/theme/interface';
import {useStyles} from '../../../shared/hooks/styles';
import {Settings} from '../../../shared/constants/config';
import {MarketTransactionHistory} from '../../../shared/models/api/dobroruResponseData';
import {dateFormats} from '../../../shared/constants/date';
import {format} from 'date-fns';

const GetStyles = (theme: Theme, headerBlockSize: number, align: AlignFunc) => {
  return useStyles(({mediaOnly}) => {
    return {
      container: {
        width: 340,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.color.borderColor,
        backgroundColor: theme.color.primaryNonGreyBackground,
        flexDirection: 'column',
        alignItems: 'stretch',
        shadowColor: theme.color.primaryBlackColor,
        shadowOpacity: 0.05,
        shadowOffset: {width: 0, height: 4},
        shadowRadius: 10,
        elevation: 5,
      },
      header: {
        height: headerBlockSize,
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      },
      headerImageBlock: {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: headerBlockSize,
      },
      logo: {
        position: 'relative',
        zIndex: 1,
        width: align(166),
        height: align(166),
      },
      body: {
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
      },
      nameContainer: {},
      name: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.h6, theme.fonts.h6),
        fontWeight: 'bold',
        color: theme.color.textColor,
        textAlign: 'center',
        textTransform: 'uppercase',
        marginTop: 10,
        ellipsizeMode: 'tail',
      },
      mainInfo: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      descriptionsContainer: {
        flex: 4,
        marginTop: 10,
        flexDirection: 'column',
        justifyContent: 'flex-start',
      },
      addressText: {
        flexWrap: 'wrap',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.bodySmallLight),
        color: theme.color.labelText,
        marginLeft: 20,
        ellipsizeMode: 'tail',
        marginBottom: 10,
      },
      dateText: {
        flexWrap: 'wrap',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.bodySmallLight),
        color: theme.color.labelText,
        marginLeft: 20,
        ellipsizeMode: 'tail',
        marginBottom: 10,
      },
      quantityText: {
        flexWrap: 'wrap',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.bodySmallLight),
        color: theme.color.labelText,
        marginLeft: 20,
        ellipsizeMode: 'tail',
      },
      priceContainer: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
      },
      priceText: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.h6, theme.fonts.h6),
        color: 'black',
        textAlign: 'right',
        alignSelf: 'center',
        overflow: 'hidden',
      },
      categoryContainer: {
        marginTop: 'auto',
        marginBottom: 10,
      },
      categoryText: {
        margin: 'auto',
        textAlign: 'center',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.body),
        color: theme.color.labelText,
      },
    };
  });
};

interface Props {
  historyItem: MarketTransactionHistory;
  style?: StyleProp<ViewStyle>;
  useHoverEffect?: boolean;
  onPress?: () => void;
}

const HEADER_BLOCK_SIZE = 205;

function _HistoryCard({
  historyItem: {operationName, cost, quantity, orderDateTime, categoryName, productImgUrl, departmentAddress},
  style,
  useHoverEffect,
  onPress,
}: Props) {
  const settings = Settings.getSettings();
  const theme = useTheme();
  const align = useAlign();
  const headerBlockSize = align(HEADER_BLOCK_SIZE);

  const styles = GetStyles(theme, headerBlockSize, align);

  const productImageUrl = React.useMemo(() => {
    return productImgUrl?.startsWith('http')
      ? productImgUrl
      : `${settings.baseUrl}Market/DownloadFile/${productImgUrl}`;
  }, [productImgUrl]);

  return (
    <Container
      testID={`history-itemCard-container`}
      onPress={onPress}
      style={[styles.container, style]}
      useHoverEffect={useHoverEffect}>
      <View testID={`history-itemCard-header`} style={styles.header}>
        <View testID={`history-itemCard-headerImageBlock`} style={styles.headerImageBlock}>
          <Image style={styles.logo} source={{uri: productImageUrl}} resizeMode='contain' />
        </View>
      </View>
      <View testID={`history-itemCard-body`} style={styles.body}>
        <View testID={`nameContainer`} style={styles.nameContainer}>
          <Text testID={`history-itemCard-name`} style={styles.name} numberOfLines={2}>
            {operationName}
          </Text>
        </View>
        <View testID={`descriptionsContainer`} style={styles.mainInfo}>
          <View style={styles.descriptionsContainer}>
            <Text testID={`history-itemCard-description`} style={styles.addressText} numberOfLines={3}>
              {stripHTML(departmentAddress)}
            </Text>
            <Text testID={`history-itemCard-description`} style={styles.dateText} numberOfLines={3}>
              {stripHTML(format(new Date(orderDateTime), dateFormats.fullDate))}
            </Text>
            <Text testID={`history-itemCard-description`} style={styles.quantityText} numberOfLines={3}>
              Получено {stripHTML(quantity.toString())} шт.
            </Text>
          </View>
          <View testID={`descriptionPriceContainer`} style={styles.priceContainer}>
            <Text testID={`history-itemCard-price`} style={styles.priceText} numberOfLines={1}>
              {`${stripHTML(String(cost))} Б`}
            </Text>
          </View>
        </View>
      </View>
      <View testID={'categoryContainer'} style={styles.categoryContainer}>
        <Text testID={`history-itemCard-categoryName`} style={styles.categoryText} numberOfLines={3}>
          {stripHTML(String(categoryName))}
        </Text>
      </View>
    </Container>
  );
}

const _CardHeaderShape: React.FC<{color: ColorValue | undefined; size: number}> = (props) => {
  if (Platform.OS === 'web') {
    const style: ViewStyle = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      bottom: 0,
      zIndex: 0,
    };
    return <images.LoyaltyAcceptorCardTracery style={style} color={props.color} />;
  }
  const scale = 2;
  const style: ViewStyle = {
    width: props.size,
    height: props.size,
    backgroundColor: props.color,
    borderRadius: props.size,
    top: -(props.size * scale - props.size) * 0.5,
    transform: [{scale}],
  };
  return <View style={style} />;
};

interface ContainerProps {
  testID: any;
  children: React.ReactFragment;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  useHoverEffect?: boolean;
}

const Container = (props: ContainerProps) => {
  const {children, onPress, useHoverEffect, style, testID} = props;

  if (onPress) {
    return (
      <TouchableOpacity
        testID={testID}
        activeOpacity={Platform.OS === 'web' ? 1 : 0.5}
        onPress={onPress}
        style={style}
        {...{dataSet: {class: useHoverEffect ? 'product-card' : 'product-card--noHover'}}}>
        {children}
      </TouchableOpacity>
    );
  }

  return <View style={style}>{children}</View>;
};

export const HistoryCard = React.memo(_HistoryCard);
